/**
 * What a tool requires, taken from the tool's own declaration.
 *
 * The bridge used to read a hand-maintained map of ~14 names. Measured live on
 * the appliance 2026-08-24: of the 184 tools the bridge advertises, 88 declare
 * `required` in their inputSchema and 8 were enforced. For the other 80 a
 * payload missing EVERY required field returned `{success:true}`.
 *
 * `export_cogo_bundle` is the case that exposed it — it declares
 * `["bundleId","format"]`, and a bare dispatch, a valid dispatch, and one with
 * an invalid enum all answered success with no effect. The party reading that
 * manifest is an AGENT, and it was being told a malformed call had worked.
 *
 * Two copies of one contract, and the second decided enforcement: the same
 * shape as a name that is emitted and never read. So the requirement is now
 * DERIVED from the manifest, and the hand map survives only as documented
 * exceptions — a tool whose handler supplies a default may relax its own
 * declaration, which is a different thing from being the only source.
 */

export interface ManifestToolEntry {
  name: string
  inputSchema?: { required?: unknown } | undefined
}

const STUDIO_NS = "cogo.studio."

export function requiredFieldsForTool(
  toolShortName: string,
  manifest: readonly ManifestToolEntry[],
  overrides: Readonly<Record<string, readonly string[]>> = {},
): readonly string[] {
  // An override wins even when it is EMPTY — that is its whole purpose, and
  // `??` would fall through on `[]` and silently re-impose the declaration.
  if (Object.prototype.hasOwnProperty.call(overrides, toolShortName)) {
    return overrides[toolShortName] ?? []
  }
  const entry = manifest.find(
    (item) => item.name === `${STUDIO_NS}${toolShortName}` || item.name === toolShortName,
  )
  const required = entry?.inputSchema?.required
  if (!Array.isArray(required)) return []
  return required.filter((field): field is string => typeof field === "string" && field.length > 0)
}
